import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { Home, Building2, Ruler, FileCheck, CheckCircle2 } from 'lucide-react';

const ProjectSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const featureIcons = [Home, Ruler, Building2, FileCheck];

  return (
    <section id="project" className="py-24 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center px-6 py-3 bg-amber-700 text-white text-sm tracking-wider uppercase">
            <FileCheck size={18} className="mr-2" />
            {t.project.badge}
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 mb-6">
            {t.project.title}
          </h2>
          <p className="text-stone-600 max-w-3xl mx-auto text-lg leading-relaxed">
            {t.project.description}
          </p>
        </div>

        {/* What's included */}
        <div className="bg-white p-8 md:p-10 mb-12 max-w-2xl mx-auto">
          <h3 className="font-serif text-xl text-stone-800 mb-6 text-center">
            {t.project.includes.title}
          </h3>
          <ul className="space-y-4">
            {t.project.includes.items.map((item, index) => (
              <li key={index} className="flex items-center text-stone-700">
                <CheckCircle2 size={20} className="text-green-600 mr-3 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {t.project.features.map((feature, index) => {
            const Icon = featureIcons[index];
            return (
              <div
                key={index}
                className="bg-white p-8 text-center group hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center text-stone-400 group-hover:text-amber-700 transition-colors">
                  <Icon size={28} strokeWidth={1.5} />
                </div>
                <div className="text-3xl font-serif text-stone-800 mb-2">
                  {feature.value}
                </div>
                <div className="text-sm text-stone-500 uppercase tracking-wider">
                  {feature.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Options */}
        <div className="mb-8">
          <h3 className="font-serif text-2xl text-stone-800 text-center mb-12">
            {t.project.options.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Option 1 */}
            <div className="bg-white p-10 border-l-4 border-amber-700">
              <div className="flex items-center mb-4">
                <Home className="text-amber-700 mr-3" size={24} strokeWidth={1.5} />
                <h4 className="font-serif text-xl text-stone-800">
                  {t.project.options.option1.title}
                </h4>
              </div>
              <p className="text-stone-600 leading-relaxed">
                {t.project.options.option1.description}
              </p>
            </div>

            {/* Option 2 */}
            <div className="bg-white p-10 border-l-4 border-stone-400">
              <div className="flex items-center mb-4">
                <Building2 className="text-stone-400 mr-3" size={24} strokeWidth={1.5} />
                <h4 className="font-serif text-xl text-stone-800">
                  {t.project.options.option2.title}
                </h4>
              </div>
              <p className="text-stone-600 leading-relaxed">
                {t.project.options.option2.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectSection;
